import {
  Component,
  computed,
  inject,
  input,
  resource,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { BrugerDatasource } from '@features/brugere/bruger.datasource';
import { LetLoadingIndicatorComponent } from '@shared/let-loading-indicator/let-loading-indicator.component';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { PanelModule } from 'primeng/panel';
import { SplitButtonModule } from 'primeng/splitbutton';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { Textarea } from 'primeng/textarea';
import { ToolbarModule } from 'primeng/toolbar';
import { BrugerHistorikComponent } from '../bruger-historik/bruger-historik.component';
import { BrugerNotifikationerComponent } from '../bruger-notifikationer/bruger-notifikationer.component';
import { BrugerRollerComponent } from '../bruger-roller/bruger-roller.component';
import { Bruger } from '../bruger.model';
import { BrugerDetailService } from './bruger-detail.service';

@Component({
  selector: 'app-bruger-detail',
  imports: [
    ConfirmDialog,
    Dialog,
    ToolbarModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    SplitButtonModule,
    InputTextModule,
    ReactiveFormsModule,
    Message,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    BrugerHistorikComponent,
    BrugerNotifikationerComponent,
    PanelModule,
    BrugerRollerComponent,
    RouterLink,
    FormsModule,
    Textarea,
    LetLoadingIndicatorComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './bruger-detail.component.html',
  styleUrl: './bruger-detail.component.css',
})
export class BrugerDetailComponent {
  confirmationService = inject(ConfirmationService);
  id = input.required<number>();
  brugerDetailService = inject(BrugerDetailService);
  datasource = inject(BrugerDatasource);
  router = inject(Router);

  validationMessage = signal<string | undefined>(undefined);
  showSaveDialog = signal(false);
  saving = signal(false);
  kommentar = signal('');
  recreatePending = signal(false);

  data = resource({
    params: () => this.id(),
    loader: async ({ params, abortSignal }) => {
      const bruger = await this.datasource.getById(params, abortSignal);
      this.form.patchValue(bruger);
      return bruger;
    },
  });

  bruger = computed(() => {
    const bruger = this.data.value();
    return bruger || ({} as Bruger);
  });

  form = new FormGroup({
    fornavn: new FormControl('', {
      validators: [Validators.required],
    }),
    efternavn: new FormControl('', {
      validators: [Validators.required],
    }),
    email: new FormControl('', {
      validators: [Validators.required, Validators.email],
    }),
  });

  get emailIsInvalid() {
    return (
      this.form.controls.email.touched &&
      this.form.controls.email.dirty &&
      this.form.controls.email.invalid
    );
  }

  get fornavnIsInvalid() {
    return (
      this.form.controls.fornavn.touched &&
      this.form.controls.fornavn.dirty &&
      this.form.controls.fornavn.invalid
    );
  }

  get efternavnIsInvalid() {
    return (
      this.form.controls.efternavn.touched &&
      this.form.controls.efternavn.dirty &&
      this.form.controls.efternavn.invalid
    );
  }

  get isLetpension() {
    return (
      !!this.form.controls.email.value?.endsWith('@letpension.dk') ||
      !!this.form.controls.email.value?.endsWith('@pfa.dk')
    );
  }

  async onClickSave() {
    this.validationMessage.set(undefined);
    if (this.form.invalid) {
      return;
    }

    this.showSaveDialog.set(true);
  }

  saveDialogIsValid = computed(() => {
    return this.kommentar() && this.kommentar().length > 4;
  });

  async saveChanges() {
    const newData = {
      ...this.bruger(),
      ...this.form.value,
    } as Bruger;

    this.saving.set(true);

    try {
      await this.brugerDetailService.saveBrugerAndRoller(
        newData,
        this.kommentar()
      );
      await this.router.navigate(['/brugeradm/brugere']);
    } catch (error) {
      console.error(error);
      let message;
      if (error instanceof Error) message = error.message;
      else message = String(error);
      this.validationMessage.set(message);
    }

    this.saving.set(false);
  }

  async onClickRecreate() {
    this.recreatePending.set(true);
    const bruger = this.data.value();
    if (bruger) {
      const response = await this.datasource.recreateBruger(bruger);
      console.log(response);
    }
    this.recreatePending.set(false);
  }

  onClickDelete() {
    this.confirmationService.confirm({
      // target: e.target as EventTarget,
      message:
        'Er du sikker at du vil slette <b>' +
        this.data.value()?.email +
        '</b>?',
      header: 'Slet bruger',
      rejectButtonProps: {
        label: 'Fortryd',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Slet',
        severity: 'danger',
      },

      accept: async () => {
        const id = this.data.value()?.id;
        if (id) {
          await this.datasource.remove(id);
          await this.router.navigate(['/brugeradm/brugere']);
        }
      },
    });
  }
}
